import { useState, useEffect } from "react";
import { Form, Input, Select, Button } from "antd";
import { useGetStaffByIdQuery } from "../../api";
import { useStaffSubmit } from "../../handlers/staffHandlers";
import type { StaffFormProps } from "../../types";

const { Option } = Select;

function StaffForm({ staffId, onSuccess }: StaffFormProps) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const { data: existingStaff } = useGetStaffByIdQuery(staffId, {
    skip: !staffId,
  });
  const { handleStaffSubmit } = useStaffSubmit();

  const isEditing = !!staffId;

  useEffect(() => {
    if (existingStaff && isEditing) {
      form.setFieldsValue({
        email: existingStaff.email,
        role: existingStaff.staffRole,
      });
    } else {
      form.resetFields();
    }
  }, [existingStaff, isEditing, form]);

  // Reset form when staffId changes (drawer closed/opened)
  useEffect(() => {
    if (!staffId) {
      form.resetFields();
    }
  }, [staffId, form]);

  const handleSubmit = async (values: any) => {
    await handleStaffSubmit(values, isEditing, staffId, setLoading, onSuccess);
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      className="space-y-4"
    >
      <Form.Item
        name="email"
        label={
          <span>
            Work Email <span className="text-red-500">*</span>
          </span>
        }
        rules={[
          { required: true, message: "Please enter the work email" },
          { type: "email", message: "Please enter a valid email address" },
        ]}
      >
        <Input placeholder="Enter work email" disabled={isEditing} />
      </Form.Item>

      <Form.Item
        name="role"
        label={
          <span>
            Role <span className="text-red-500">*</span>
          </span>
        }
        rules={[{ required: true, message: "Please select a role" }]}
      >
        <Select placeholder="Select a role">
          <Option value="ADMIN">Admin</Option>
          <Option value="VIEWER">Viewer</Option>
        </Select>
      </Form.Item>

      <Form.Item className="mb-0">
        <div className="flex justify-end gap-3">
          <Button type="default" onClick={onSuccess}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            {isEditing ? "Update Staff" : "Invite Staff"}
          </Button>
        </div>
      </Form.Item>
    </Form>
  );
}

export default StaffForm;
